<template>
  <div class="variants__wrapper">
    <p
      class="variants__label"
      :class="{
        'variants__label--sold-out': isSoldOut,
      }"
    >
      Size: <span>{{ variantStatus }}</span>
    </p>
    <div class="variants">
      <Button
        v-for="variant in activeVariants"
        :key="variant.id"
        width="full"
        theme="secondary"
        form="outline"
        class="variant"
        :class="{
          'variant--disabled': variantDisabled(variant),
          'variant--selected': activeSelectedVariant?.id === variant.id,
        }"
        @click="handleVariantSelect(variant)"
        @mouseenter="hoveredVariant = variant"
        @mouseleave="hoveredVariant = null"
      >
        {{ variant.title }}
      </Button>
    </div>
    <VariantsMeta v-if="!hideMedia" />
  </div>
</template>

<script>
// Import components
import Button from "@/components/ButtonV2.vue";
import VariantsMeta from "@/components/product-display/VariantsMeta.vue";

// Import internal modules
import { mapState, mapActions, mapGetters } from "vuex";
import { unwrapNodes } from "@/helpers/graphql";

export default {
  name: "Variants",
  components: {
    Button,
    VariantsMeta,
  },
  props: {
    hideMedia: {
      type: Boolean,
      default: false,
    },
    /** Use quick-look product/variant instead of the main PDP product. */
    isolated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hoveredVariant: null,
    };
  },
  computed: {
    ...mapState("product", [
      "product",
      "selectedVariant",
      "quickLookProduct",
      "quickLookSelectedVariant",
    ]),
    ...mapGetters("product", ["variants"]),
    activeProduct() {
      return this.isolated ? this.quickLookProduct : this.product;
    },
    activeSelectedVariant() {
      return this.isolated
        ? this.quickLookSelectedVariant
        : this.selectedVariant;
    },
    activeVariants() {
      if (!this.isolated) {
        return this.variants;
      }

      const variants =
        unwrapNodes(this.quickLookProduct?.variants?.edges) || [];

      return variants.map((variant) => {
        const title = variant.title.split(" / ")[0];
        return {
          ...variant,
          title,
        };
      });
    },
    statusVariant() {
      return this.hoveredVariant || this.activeSelectedVariant;
    },
    variantStatus() {
      if (!this.statusVariant || !this.quantityStatus) {
        return null;
      }

      return `${this.statusVariant.title} ${this.quantityStatus}`;
    },
    quantityStatus() {
      const variant = this.statusVariant;
      const quantity = variant?.quantityAvailable;

      switch (true) {
        case quantity <= 0 && this.availableForPreOrder(variant):
          return "Available for preorder";
        case quantity <= 0:
          return "Out of stock";
        case quantity <= 15 && quantity > 5:
          return "Low stock";
        default:
          return null;
      }
    },
    isSoldOut() {
      const variant = this.statusVariant;

      if (!variant || this.availableForPreOrder(variant)) {
        return false;
      }

      return !variant.availableForSale || variant.quantityAvailable <= 0;
    },
  },
  methods: {
    ...mapActions("product", [
      "selectVariantForPurchase",
      "setQuickLookSelectedVariant",
    ]),
    handleVariantSelect(variant) {
      if (this.isolated) {
        this.setQuickLookSelectedVariant(variant);
        return;
      }

      this.selectVariantForPurchase(variant);
    },
    variantDisabled(variant) {
      const variantIsPreOrder = this.availableForPreOrder(variant);

      if (variantIsPreOrder) {
        return false;
      }

      return !variant.availableForSale || variant.quantityAvailable <= 0;
    },
    preOrder(variant) {
      const preorder = variant?.randbPreorder?.value;
      if (!preorder) return false;

      return JSON.parse(preorder);
    },
    availableForPreOrder(variant) {
      if (this.activeProduct?.tags?.includes("hide-preorder")) return false;

      const {
        enabled = false,
        limit = 0,
        reserved = 0,
      } = this.preOrder(variant);
      const inventory = variant.quantityAvailable || 0;

      return enabled && inventory <= 0 && reserved < limit;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form";

.variants {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 10px;
  justify-items: flex-start;
  align-items: center;

  & > * {
    display: block;
    width: 100%;
    word-wrap: break-word;
  }
}

.variants__wrapper {
  margin-top: 24px;
}

.variant {
  padding-left: 0;
  padding-right: 0;

  &--disabled {
    background-color: $color-grey-light;
    border-color: $color-grey;
    color: $color-grey;
    text-decoration: line-through;

    :deep(.text) {
      text-decoration: line-through;
    }

    &:hover {
      background-color: $color-grey-light;
      border-color: $color-grey;
      color: $color-grey;
    }
  }

  &--selected.variant--disabled {
    border-color: $color-black;
    background-color: $color-grey-light;
    color: $color-black;

    &:hover {
      border-color: $color-black;
      background-color: $color-grey-light;
      color: $color-black;
    }
  }

  &--selected {
    border-color: $color-grey-dark;
  }
}

.variants__label {
  @include utility-label(14px);
  margin-bottom: 8px;

  &--sold-out span {
    color: $color-red;
  }
}
</style>
